import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './IESIntegrationDashboard.css';

/**
 * IES Integration Dashboard
 * 
 * End-to-End Data Pipeline Monitoring with Data Validation
 * 
 * Pipelines:
 * 1. IRMA → DML → IES (S3 files)
 * 2. Workday → DML → IES (Employee data)
 * 3-7. SAP → DML → IES (5 integrations):
 *    - Networks
 *    - Activity Milestones
 *    - Characteristics
 *    - Projects
 *    - Salesorder
 * 
 * Data Validation: Compares data at each stage to identify missing records
 */

// Max list items rendered per comparison column. High enough to show the full
// (de-duplicated) record set so users can scroll through everything; guards
// against pathological DOM sizes. Real datasets can exceed 9k records, so keep
// this well above expected volumes to avoid the "+N more" cut-off making the
// tail of the list unreachable.
const MAX_RENDER = 50000;

// Canonicalise an id before comparing across systems. SAP-sourced ids on the
// DML side are zero-padded (e.g. "000073064431") while the same identifier in
// the IES DynamoDB attribute is stored without padding (e.g. "73064431"). To
// avoid false "missing"/"extra" from pure formatting differences, we compare on
// a normalized form: trim whitespace, and for purely numeric ids strip leading
// zeros. Non-numeric ids (filenames, etc.) are returned trimmed as-is.
const normalizeId = (id) => {
  if (id === null || id === undefined) return '';
  const s = String(id).trim();
  // All digits (optionally with leading zeros) -> strip leading zeros.
  if (/^\d+$/.test(s)) {
    const stripped = s.replace(/^0+/, '');
    return stripped === '' ? '0' : stripped;
  }
  return s;
};

// De-duplicate on the NORMALIZED id (see normalizeId) so the same business id
// in different formats (e.g. zero-padded "000078331361" vs "78331361") is shown
// only once. Keeps the first-seen display form and preserves order.
const uniqNormalized = (arr) => {
  const seen = new Set();
  const out = [];
  for (const v of (Array.isArray(arr) ? arr : [])) {
    const k = normalizeId(v);
    if (!seen.has(k)) {
      seen.add(k);
      out.push(v);
    }
  }
  return out;
};

// De-duplicate a list of file objects by filename/key, preserving order + size.
const uniqByKey = (arr) => {
  const seen = new Set();
  const out = [];
  for (const f of (Array.isArray(arr) ? arr : [])) {
    const k = (f && (f.filename || f.key)) || f;
    if (!seen.has(k)) {
      seen.add(k);
      out.push(f);
    }
  }
  return out;
};

// Derive one consistent set of SAP counts from the day's data so the header,
// comparison columns, IES badge, and mismatch pill all read the same numbers.
//
// When the day was verified with a targeted method (batch_get/query/scan_diff)
// and the authoritative totals are present, counts come from those totals
// (dmlCount, missingInIesCount) rather than the capped id-sample lengths. The
// capped `ids` arrays remain the source only for rendering the visible id rows.
//
// Otherwise (low-volume, non-targeted, or missing totals) it falls back to the
// deduped sample lengths, preserving today's behavior and signalling the caller
// (via missingCount = null) to keep using the client-side set-diff.
//
// Pure function (data + two sample lengths in, plain object out) so it can be
// unit-tested independently of React rendering.
export const deriveSapCounts = (data, dmlSampleLen, iesSampleLen) => {
  const d = data || {};
  const isTargeted = (m) => m === 'batch_get' || m === 'query' || m === 'scan_diff';
  const usedTargetedVerify = isTargeted(d.verifyMethod) || isTargeted(d.ies && d.ies.method);

  if (usedTargetedVerify
      && typeof d.missingInIesCount === 'number'
      && typeof d.dmlCount === 'number') {
    return {
      usedTargetedVerify: true,
      dmlColumnCount: d.dmlCount,
      // True present count; guard against negatives.
      iesColumnCount: Math.max(0, d.dmlCount - d.missingInIesCount),
      missingCount: d.missingInIesCount
    };
  }

  return {
    usedTargetedVerify,
    dmlColumnCount: dmlSampleLen || d.dmlCount || '—',
    iesColumnCount: iesSampleLen || d.iesCount || 0,
    // null signals the caller to fall back to the client-side set-diff count.
    missingCount: null
  };
};

// Pipeline configurations
const PIPELINE_CONFIG = {
  irma: {
    id: 'irma-dml-ies',
    name: 'IRMA → DML → IES',
    icon: '📁',
    category: 'irma',
    dataType: 'files',
    apiEndpoint: '/api/irma-dml/data',
    stages: [
      { id: 'irma', name: 'IRMA', icon: '🏢', label: 'source files' },
      { id: 'dml', name: 'DML S3', icon: '🪣', label: 'transfer files' },
      { id: 'ies', name: 'IES S3', icon: '🪣', label: 'destination files' }
    ]
  },
  workday: {
    id: 'workday-dml-ies',
    name: 'Workday → DML → IES',
    icon: '👥',
    category: 'workday',
    dataType: 'employees',
    apiEndpoint: '/api/dml-msk/data',
    integrationParam: 'employee',  // Query param for DML-MSK API
    stages: [
      { id: 'workday', name: 'Workday', icon: '💼', label: 'employee records' },
      { id: 'dml', name: 'DML S3', icon: '🪣', label: 'processed records' },
      { id: 'ies', name: 'IES DynamoDB', icon: '🗄️', label: 'modified employees' }
    ]
  },
  // SAP Integrations - Data Flow: DML Raw S3 → DML Processed S3 → IES
  // All use the same DML-MSK API with different integration parameters
  networks: {
    id: 'sap-networks',
    name: 'SAP Networks',
    icon: '🌐',
    category: 'sap',
    dataType: 'networks',
    apiEndpoint: '/api/dml-msk/data',
    integrationParam: 'networks',
    stages: [
      { id: 'dml-raw', name: 'DML Raw S3', icon: '📥', label: 'raw from SAP' },
      { id: 'dml-processed', name: 'DML Processed S3', icon: '⚡', label: 'after business logic' },
      { id: 'ies', name: 'IES DynamoDB', icon: '🗄️', label: 'destination records' }
    ]
  },
  activityMilestones: {
    id: 'sap-activity-milestones',
    name: 'SAP Activity Milestones',
    icon: '🎯',
    category: 'sap',
    dataType: 'activity_milestones',
    apiEndpoint: '/api/dml-msk/data',
    integrationParam: 'activity_milestones',
    stages: [
      { id: 'dml-raw', name: 'DML Raw S3', icon: '📥', label: 'raw from SAP' },
      { id: 'dml-processed', name: 'DML Processed S3', icon: '⚡', label: 'after business logic' },
      { id: 'ies', name: 'IES DynamoDB', icon: '🗄️', label: 'destination records' }
    ]
  },
  characteristics: {
    id: 'sap-characteristics',
    name: 'SAP Characteristics',
    icon: '📋',
    category: 'sap',
    dataType: 'characteristics',
    apiEndpoint: '/api/dml-msk/data',
    integrationParam: 'characteristics',
    stages: [
      { id: 'dml-raw', name: 'DML Raw S3', icon: '📥', label: 'raw from SAP' },
      { id: 'dml-processed', name: 'DML Processed S3', icon: '⚡', label: 'after business logic' },
      { id: 'ies', name: 'IES DynamoDB', icon: '🗄️', label: 'destination records' }
    ]
  },
  projects: {
    id: 'sap-projects',
    name: 'SAP Projects',
    icon: '📊',
    category: 'sap',
    dataType: 'projects',
    apiEndpoint: '/api/dml-msk/data',
    integrationParam: 'projects',
    stages: [
      { id: 'dml-raw', name: 'DML Raw S3', icon: '📥', label: 'raw from SAP' },
      { id: 'dml-processed', name: 'DML Processed S3', icon: '⚡', label: 'after business logic' },
      { id: 'ies', name: 'IES DynamoDB', icon: '🗄️', label: 'destination records' }
    ]
  },
  salesorder: {
    id: 'sap-salesorder',
    name: 'SAP Salesorder',
    icon: '🛒',
    category: 'sap',
    dataType: 'salesorder',
    apiEndpoint: '/api/dml-msk/data',
    integrationParam: 'salesorder',
    stages: [
      { id: 'dml-raw', name: 'DML Raw S3', icon: '📥', label: 'raw from SAP' },
      { id: 'dml-processed', name: 'DML Processed S3', icon: '⚡', label: 'after business logic' },
      { id: 'ies', name: 'IES DynamoDB', icon: '🗄️', label: 'destination records' }
    ]
  }
};

const IESIntegrationDashboard = ({ teamName, environment, apiUrl }) => {
  // State for all pipelines
  const [pipelineData, setPipelineData] = useState({});
  const [loading, setLoading] = useState({});
  const [errors, setErrors] = useState({});
  const [selectedDates, setSelectedDates] = useState({});
  const [expandedPipelines, setExpandedPipelines] = useState({});
  const [lastRefresh, setLastRefresh] = useState(null);

  // Initialize dates and expand all pipelines by default
  useEffect(() => {
    const yesterday = getYesterdayDate();
    const initialDates = {};
    const initialExpanded = {};
    Object.keys(PIPELINE_CONFIG).forEach(key => {
      initialDates[key] = yesterday;
      initialExpanded[key] = true;  // All pipelines expanded by default
    });
    setSelectedDates(initialDates);
    setExpandedPipelines(initialExpanded);
  }, []);

  function getYesterdayDate() {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday.toISOString().split('T')[0];
  }

  function getTodayDate() {
    return new Date().toISOString().split('T')[0];
  }

  // Check if data collection has run today (runs at 10 AM IST = 4:30 AM UTC)
  // Returns true if current time is after 10 AM IST
  function isAfterDataCollectionTime() {
    const now = new Date();
    // Convert current time to IST (UTC + 5:30)
    const utcHours = now.getUTCHours();
    const utcMinutes = now.getUTCMinutes();
    // IST is UTC + 5:30, so 10:00 IST = 04:30 UTC
    const istHours = utcHours + 5 + Math.floor((utcMinutes + 30) / 60);
    const adjustedIstHours = istHours >= 24 ? istHours - 24 : istHours;
    // Data collection runs at 10 AM IST
    return adjustedIstHours >= 10;
  }

  // Filter out today's date from data array if data collection hasn't run yet
  function filterDataBeforeCollection(dataArray) {
    if (!Array.isArray(dataArray)) return [];
    const today = getTodayDate();
    // If it's before 10 AM IST, exclude today's date
    if (!isAfterDataCollectionTime()) {
      return dataArray.filter(item => item.date !== today);
    }
    return dataArray;
  }

  // Generate 7 days of placeholder data for a pipeline
  const generate7DaysPlaceholder = useCallback((pipelineKey) => {
    const days = [];
    for (let i = 1; i <= 7; i++) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];
      days.push({
        date: dateStr,
        status: 'pending',
        rawRecordIds: [],
        processedRecordIds: [],
        iesRecordIds: [],
        iesCount: 0,
        rawCount: 0,
        processedCount: 0
      });
    }
    return days;
  }, []);

  // Load data for a specific pipeline
  const loadPipelineData = useCallback(async (pipelineKey) => {
    const config = PIPELINE_CONFIG[pipelineKey];
    if (!config) return;

    setLoading(prev => ({ ...prev, [pipelineKey]: true }));
    setErrors(prev => ({ ...prev, [pipelineKey]: null }));

    try {
      // Build params - include integration parameter for DML-MSK API
      const params = { days: 7 };
      if (config.integrationParam) {
        params.integration = config.integrationParam;
      }
      
      const response = await axios.get(`${apiUrl}${config.apiEndpoint}`, { params });
      const responseData = response.data.data || response.data || [];
      // Ensure it's an array
      setPipelineData(prev => ({ 
        ...prev, 
        [pipelineKey]: Array.isArray(responseData) ? responseData : [] 
      }));
      setLastRefresh(new Date());
    } catch (err) {
      console.error(`Error loading ${pipelineKey} data:`, err);
      // For SAP integrations that aren't implemented yet, generate 7 days of placeholder data
      // so users can still see the date selection and the IES data from DynamoDB collector
      if (config.category === 'sap' || err.response?.status === 404 || err.message?.includes('Network Error')) {
        // Generate 7 days placeholder so date buttons show up
        const placeholderData = generate7DaysPlaceholder(pipelineKey);
        setPipelineData(prev => ({ ...prev, [pipelineKey]: placeholderData }));
        // Don't show error for unimplemented SAP endpoints - just show empty state
        if (config.category !== 'sap') {
          setErrors(prev => ({
            ...prev,
            [pipelineKey]: err.response?.status === 503
              ? `${config.name} service not available.`
              : `Failed to load ${config.name} data.`
          }));
        }
      } else {
        setErrors(prev => ({
          ...prev,
          [pipelineKey]: err.response?.status === 503
            ? `${config.name} service not available.`
            : `Failed to load ${config.name} data.`
        }));
      }
    } finally {
      setLoading(prev => ({ ...prev, [pipelineKey]: false }));
    }
  }, [apiUrl, generate7DaysPlaceholder]);

  // Load IES DynamoDB data (collected daily at 10 AM IST)
  const loadIesDynamoDbData = useCallback(async () => {
    try {
      const response = await axios.get(`${apiUrl}/api/ies-data/latest`);
      const iesData = response.data;
      
      if (iesData && iesData.integrations) {
        // Map IES DynamoDB data to pipeline data format
        // This data contains record IDs from IES DynamoDB tables
        const integrationMapping = {
          'employees': 'workday',
          'networks': 'networks',
          'activity_milestones': 'activityMilestones',
          'characteristics': 'characteristics',
          'projects': 'projects',
          'salesorder': 'salesorder'
        };

        // Update pipeline data with IES record IDs
        setPipelineData(prev => {
          const updated = { ...prev };
          
          iesData.integrations.forEach(integration => {
            const pipelineKey = integrationMapping[integration.integration];
            if (pipelineKey) {
              // Initialize pipeline data if it doesn't exist
              if (!updated[pipelineKey]) {
                updated[pipelineKey] = [];
              }
              
              // Merge IES data into existing pipeline data
              const existingData = Array.isArray(updated[pipelineKey]) ? updated[pipelineKey] : [];
              const dateData = existingData.find(d => d.date === integration.date);
              
              if (dateData) {
                // Update existing date entry with IES record IDs
                dateData.iesRecordIds = integration.iesRecordIds || [];
                dateData.iesCount = integration.iesCount || 0;
                dateData.iesIdField = integration.id_field || '';  // DynamoDB column name
                dateData.iesTableName = integration.table_name || '';  // DynamoDB table name
                // For workday, also set iesEmployeeIds
                if (pipelineKey === 'workday') {
                  dateData.iesEmployeeIds = integration.iesRecordIds || [];
                }
              } else {
                // Add new date entry with IES data
                const newEntry = {
                  date: integration.date,
                  iesRecordIds: integration.iesRecordIds || [],
                  iesCount: integration.iesCount || 0,
                  iesIdField: integration.id_field || '',  // DynamoDB column name
                  iesTableName: integration.table_name || '',  // DynamoDB table name
                  status: integration.status || 'success',
                  // Initialize empty arrays for other columns
                  rawRecordIds: [],
                  processedRecordIds: []
                };
                if (pipelineKey === 'workday') {
                  newEntry.iesEmployeeIds = integration.iesRecordIds || [];
                  newEntry.workdayEmployeeIds = [];
                  newEntry.dmlEmployeeIds = [];
                }
                existingData.push(newEntry);
              }
              
              updated[pipelineKey] = existingData;
            }
          });
          
          return updated;
        });
        
        console.log('IES DynamoDB data loaded:', iesData.summary);
      }
    } catch (err) {
      console.error('Error loading IES DynamoDB data:', err);
      // Don't show error - this is supplementary data
    }
  }, [apiUrl]);

  // Load all pipeline data
  useEffect(() => {
    Object.keys(PIPELINE_CONFIG).forEach(key => {
      loadPipelineData(key);
    });
    // Also load IES DynamoDB data
    loadIesDynamoDbData();
  }, [loadPipelineData, loadIesDynamoDbData]);

  // Toggle pipeline expansion
  const togglePipeline = (pipelineKey) => {
    setExpandedPipelines(prev => ({
      ...prev,
      [pipelineKey]: !prev[pipelineKey]
    }));
  };

  // Get status helpers
  const getStatusIcon = (status, count) => {
    if (status === 'error') return '❌';
    if (status === 'data_loss') return '🚨';
    if (count === 0 || count === null || count === undefined) return '⚠️';
    return '✅';
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const formatSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Calculate validation status between stages
  const calculateValidation = (data) => {
    if (!data || !data.stages || !Array.isArray(data.stages)) return null;

    const stages = data.stages;
    const validation = {
      isValid: true,
      missingRecords: [],
      extraRecords: [],
      stageComparisons: []
    };

    // Compare consecutive stages
    for (let i = 0; i < stages.length - 1; i++) {
      const sourceStage = stages[i];
      const destStage = stages[i + 1];
      
      // Compare on the normalized id form (see normalizeId) so formatting
      // differences across systems don't create false data-loss.
      const sourceIds = new Set((sourceStage.recordIds || []).map(normalizeId));
      const destIds = new Set((destStage.recordIds || []).map(normalizeId));
      
      const missingInDest = (sourceStage.recordIds || []).filter(id => !destIds.has(normalizeId(id)));
      const extraInDest = (destStage.recordIds || []).filter(id => !sourceIds.has(normalizeId(id)));
      
      const comparison = {
        from: sourceStage.name,
        to: destStage.name,
        sourceCount: sourceStage.count || sourceIds.size,
        destCount: destStage.count || destIds.size,
        missing: missingInDest,
        extra: extraInDest,
        status: missingInDest.length === 0 ? 'healthy' : 'data_loss'
      };
      
      validation.stageComparisons.push(comparison);
      
      if (missingInDest.length > 0) {
        validation.isValid = false;
        validation.missingRecords.push(...missingInDest.map(id => ({
          id,
          lostAt: destStage.name,
          fromStage: sourceStage.name
        })));
      }
    }

    return validation;
  };

  // Compare records across 3 columns and return validation status.
  // Membership is tested on the NORMALIZED id (see normalizeId) so that pure
  // formatting differences between systems (e.g. zero-padded DML networkNumber
  // vs unpadded IES networkNumber) don't produce false missing/extra results.
  // The returned id lists keep their ORIGINAL display form.
  const compareColumnRecords = (sourceIds, middleIds, destIds) => {
    const source = new Set((sourceIds || []).map(normalizeId));
    const middle = new Set((middleIds || []).map(normalizeId));
    const dest = new Set((destIds || []).map(normalizeId));
    
    // Records missing at each stage (compare normalized, keep original for display).
    const missingInMiddle = (sourceIds || []).filter(id => !middle.has(normalizeId(id)));
    const missingInDest = (middleIds || []).filter(id => !dest.has(normalizeId(id)));
    
    // Extra records (in dest but not in source chain)
    const extraInDest = (destIds || []).filter(id => !middle.has(normalizeId(id)) && !source.has(normalizeId(id)));
    
    // Matched records (present in all 3)
    const matchedAll = (sourceIds || []).filter(id => middle.has(normalizeId(id)) && dest.has(normalizeId(id)));
    
    return {
      sourceCount: source.size,
      middleCount: middle.size,
      destCount: dest.size,
      missingInMiddle,
      missingInDest,
      extraInDest,
      matchedAll,
      isValid: missingInMiddle.length === 0 && missingInDest.length === 0,
      totalMissing: missingInMiddle.length + missingInDest.length
    };
  };

  // Get validation status badge for a column
  const getColumnStatusBadge = (comparison, columnType) => {
    if (!comparison) return null;
    
    if (columnType === 'source') {
      return comparison.sourceCount > 0 ? (
        <span className="column-status-badge match">✓ Source</span>
      ) : null;
    }
    
    if (columnType === 'middle') {
      if (comparison.missingInMiddle.length > 0) {
        return (
          <span className="column-status-badge missing">
            ⚠ {comparison.missingInMiddle.length} missing
          </span>
        );
      }
      return comparison.middleCount > 0 ? (
        <span className="column-status-badge match">✓ OK</span>
      ) : null;
    }
    
    if (columnType === 'dest') {
      if (comparison.missingInDest.length > 0) {
        return (
          <span className="column-status-badge missing">
            ⚠ {comparison.missingInDest.length} missing
          </span>
        );
      }
      if (comparison.extraInDest.length > 0) {
        return (
          <span className="column-status-badge extra">
            + {comparison.extraInDest.length} extra
          </span>
        );
      }
      return comparison.destCount > 0 ? (
        <span className="column-status-badge match">✓ OK</span>
      ) : null;
    }
    
    return null;
  };

  // Compute DML -> IES mismatch (records/files present in DML but missing in IES).
  // Drives both the "Missing in IES" column and the integration status pill.
  const getPipelineMismatch = (pipelineKey, data, config) => {
    if (!data) return null;
    let referenceIds = [];  // DML side - what should have reached IES
    let destIds = [];       // IES side - what actually arrived
    if (pipelineKey === 'irma') {
      const dmlFiles = data.dmlFiles || data.files || [];
      const iesFiles = data.iesFiles || [];
      referenceIds = dmlFiles.map(f => f.filename || f.key || f);
      destIds = iesFiles.map(f => f.filename || f.key || f);
    } else if (pipelineKey === 'workday') {
      referenceIds = data.dmlEmployeeIds || data.dml?.ids || [];
      destIds = data.iesEmployeeIds || data.ies?.ids || [];
    } else if (config && config.category === 'sap') {
      referenceIds = data.rawRecordIds || data.dml?.ids || [];
      destIds = data.iesRecordIds || data.ies?.ids || [];
    } else {
      return null;
    }

    // The collector now verifies each DML id directly against the IES DynamoDB
    // table (targeted key lookup) and returns the authoritative "missing in IES"
    // list. Prefer it when present - it reflects real table membership, not a
    // sampled modifiedAt scan. `verifyMethod` batch_get/query means the backend
    // did the targeted verification for this record.
    const backendMissing = data.missingInIes || data.ies?.missingIds;
    const usedTargetedVerify = data.verifyMethod === 'batch_get'
      || data.verifyMethod === 'query'
      || data.verifyMethod === 'scan_diff'
      || data.ies?.method === 'batch_get'
      || data.ies?.method === 'query'
      || data.ies?.method === 'scan_diff';
    if (usedTargetedVerify && Array.isArray(backendMissing)) {
      return {
        // Authoritative totals: the id arrays may be capped at MAX_SAMPLE_IDS,
        // so prefer the true counts and derive the present count from them.
        sourceCount: (data.dmlCount != null)
          ? data.dmlCount
          : (referenceIds.length || data.dmlCount || 0),
        destCount: (data.dmlCount != null && data.missingInIesCount != null)
          ? Math.max(0, data.dmlCount - data.missingInIesCount)
          : (destIds.length || data.iesCount || 0),
        missing: backendMissing,
        missingCount: (data.missingInIesCount != null)
          ? data.missingInIesCount
          : backendMissing.length,
        hasMismatch: ((data.missingInIesCount != null)
          ? data.missingInIesCount
          : backendMissing.length) > 0
      };
    }

    // Fallback: compare on the normalized id form so cross-system formatting
    // differences (e.g. zero-padded DML ids vs unpadded IES ids) don't flag a
    // false mismatch.
    const destSet = new Set(destIds.map(normalizeId));
    const uniqueRef = [...new Set(referenceIds.map(normalizeId))];
    const missing = uniqueRef.filter(id => !destSet.has(id));

    // Guard against false positives: if IES reports a count but its IDs were not
    // loaded, we cannot reliably diff, so don't flag a mismatch.
    const iesIdsLoaded = destIds.length > 0 || !((data.iesCount || data.count || 0) > 0);

    return {
      sourceCount: referenceIds.length,
      destCount: destIds.length,
      missing,
      missingCount: missing.length,
      hasMismatch: iesIdsLoaded && uniqueRef.length > 0 && missing.length > 0
    };
  };

  // Get overall status for top summary
  const getOverallStatus = (data, countField = 'count', mismatch = null) => {
    if (!data) return { status: 'pending', label: 'PENDING', color: '#f59e0b' };
    const count = data[countField] || data.iesCount || data.fileCount || 0;
    if (data.status === 'error') return { status: 'error', label: 'ERROR', color: '#ef4444' };
    // Records reached DML but did not arrive in IES -> surface a clear mismatch status.
    if (mismatch && mismatch.hasMismatch) {
      return { status: 'data_mismatch', label: `MISMATCH · ${mismatch.missingCount} missing`, color: '#ef4444' };
    }
    if (data.validation && !data.validation.isValid) return { status: 'data_loss', label: 'DATA LOSS', color: '#ef4444' };
    if (data.status === 'success' || data.status === 'healthy') {
      return count > 0
        ? { status: 'success', label: 'SUCCESS', color: '#22c55e' }
        : { status: 'no_data', label: 'NO DATA', color: '#ef4444' };
    }
    // Comparison ran clean (data on both sides, nothing missing) -> healthy.
    if (mismatch && mismatch.sourceCount > 0 && mismatch.destCount > 0 && !mismatch.hasMismatch) {
      return { status: 'success', label: 'SUCCESS', color: '#22c55e' };
    }
    // This is a collected day (a data record exists). Non-zero -> healthy;
    // zero records -> failed (no data flowed), shown red.
    if (count > 0) {
      return { status: 'success', label: 'SUCCESS', color: '#22c55e' };
    }
    return { status: 'no_data', label: 'NO DATA', color: '#ef4444' };
  };

  // Distinct destination (IES) id count for a given day, matching exactly what
  // the comparison columns display. Returns null when the ids were not loaded
  // (so callers can fall back to the raw stored count).
  const getSelectedDayCount = (pipelineKey, data) => {
    if (!data) return null;
    let destIds;
    if (pipelineKey === 'irma') {
      const iesFiles = data.iesFiles || [];
      destIds = iesFiles.map(f => f.filename || f.key || f);
    } else if (pipelineKey === 'workday') {
      destIds = data.iesEmployeeIds || data.ies?.ids || [];
    } else {
      // SAP integrations
      destIds = data.iesRecordIds || data.ies?.ids || [];
    }
    if (!Array.isArray(destIds) || destIds.length === 0) return null;
    // Distinct, normalized (same as the comparison set-diff) so composite-key
    // tables (characteristics/milestones/sales-orders) report distinct business
    // ids, not raw row counts.
    return new Set(destIds.map(normalizeId)).size;
  };

  // Single source of truth for the number shown on a day (header "Records" AND
  // the day-selector buttons). For SAP days verified with a targeted method the
  // id sample is capped at MAX_SAMPLE_IDS, so the distinct-sample length would
  // understate the true present count (clamping at 1000). In that case prefer the
  // authoritative present count (dmlCount − missingInIesCount) via deriveSapCounts;
  // otherwise fall back to the distinct sample length, then the raw stored count.
  const getDayDisplayCount = (pipelineKey, config, data) => {
    if (!data) return 0;
    const distinct = getSelectedDayCount(pipelineKey, data);
    if (config && config.category === 'sap') {
      const sap = deriveSapCounts(data, distinct || 0, distinct || 0);
      if (sap.usedTargetedVerify && typeof sap.iesColumnCount === 'number') {
        return sap.iesColumnCount;
      }
    }
    return distinct != null
      ? distinct
      : (data.iesCount || data.fileCount || data.count || 0);
  };

  // Render pipeline overview card
  const renderPipelineOverview = (pipelineKey, config) => {
    const data = pipelineData[pipelineKey] || [];
    // Ensure data is an array and filter out today if before data collection time (10 AM IST)
    const rawDataArray = Array.isArray(data) ? data : [];
    const dataArray = filterDataBeforeCollection(rawDataArray);
    const selectedData = dataArray.find(d => d.date === selectedDates[pipelineKey]);
    const mismatch = getPipelineMismatch(pipelineKey, selectedData, config);
    const status = getOverallStatus(selectedData, 'count', mismatch);
    const isExpanded = expandedPipelines[pipelineKey];
    // Header "Records" must match what the comparison columns show: the DISTINCT
    // destination id count. Tables with a composite sort key (e.g. characteristics
    // keyed by networkNumber+sk) store many rows per business id, so the raw
    // iesCount (row count) is far larger than the distinct id count. Prefer the
    // distinct id list length (same source the columns use); fall back to the raw
    // count only when the ids were not loaded.
    // Authoritative day count (handles the SAP 1000-sample cap). Same helper the
    // day-selector buttons use, so header and buttons never disagree.
    const count = getDayDisplayCount(pipelineKey, config, selectedData);
    // SAP integrations are always enabled; status is driven by data/mismatch below.
    const isNotConfigured = false;
    const hasIssue = ['error', 'data_mismatch', 'data_loss'].includes(status.status);
    // Colour the whole header by flow health: green when healthy, red when the
    // flow has failed (error / mismatch / data loss / no data), amber while
    // still pending today's collection.
    const healthClass = status.status === 'success'
      ? 'health-ok'
      : (status.status === 'pending' ? 'health-warn' : 'health-failed');

    return (
      <div key={pipelineKey} className={`pipeline-overview-card ${isExpanded ? 'expanded' : ''} ${hasIssue ? 'has-issue' : ''}`}>
        <div className={`pipeline-overview-header ${healthClass}`} onClick={() => togglePipeline(pipelineKey)}>
          <div className="pipeline-info">
            <div className="pipeline-details">
              <span className="pipeline-name">
                {hasIssue && <span className="issue-flag" title="Integration issue detected">⚠️</span>}
                {config.name}
              </span>
              <span className="pipeline-category">{config.category.toUpperCase()}</span>
            </div>
          </div>
          <div className="pipeline-stats">
            {isNotConfigured ? (
              <span className="status-pill" style={{ backgroundColor: '#9ca3af' }}>
                NOT CONFIGURED
              </span>
            ) : (
              <>
                <div className="stat-item">
                  <span className="stat-value">{count.toLocaleString()}</span>
                  <span className="stat-label">Records</span>
                </div>
                <span className="status-pill" style={{ backgroundColor: status.color }}>
                  {status.label}
                </span>
              </>
            )}
            <span className="expand-icon">{isExpanded ? '▼' : '▶'}</span>
          </div>
        </div>

        {isExpanded && (
          <div className="pipeline-details-section">
            {renderPipelineDetails(pipelineKey, config)}
          </div>
        )}
      </div>
    );
  };

  // Render detailed pipeline view
  const renderPipelineDetails = (pipelineKey, config) => {
    const data = pipelineData[pipelineKey] || [];
    // Ensure data is an array and filter out today if before data collection time (10 AM IST)
    const rawDataArray = Array.isArray(data) ? data : [];
    const dataArray = filterDataBeforeCollection(rawDataArray);
    const selectedData = dataArray.find(d => d.date === selectedDates[pipelineKey]);
    const isLoading = loading[pipelineKey];
    const error = errors[pipelineKey];

    // Calculate validation
    const validation = selectedData ? calculateValidation(selectedData) : null;

    return (
      <div className="pipeline-detail-content">
        {/* Controls removed - using date quick select buttons instead */}

        {error && (
          <div className="alert alert-error">
            <span>⚠️ {error}</span>
            <button onClick={() => setErrors(prev => ({ ...prev, [pipelineKey]: null }))}>×</button>
          </div>
        )}

        {isLoading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading {config.name} data...</p>
          </div>
        ) : (
          <>
            {/* Unified Pipeline Widget - Diagram + Table Combined */}
            <div className="unified-pipeline-widget">
              {/* Compact Data Flow Visualization - Shows pipeline stages without counts */}
              <div className="data-flow-compact">
                {config.stages.map((stage, index) => (
                  <React.Fragment key={stage.id}>
                    <div className="stage-box-compact">
                      <div className="stage-icon-compact">{stage.icon}</div>
                      <div className="stage-info-compact">
                        <span className="stage-name-compact">{stage.name}</span>
                      </div>
                    </div>
                    {index < config.stages.length - 1 && (
                      <div className="flow-arrow-compact">
                        <span className="arrow-compact">→</span>
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>

              {/* Automatic Validation Summary - shown when there's data loss */}
              {validation && !validation.isValid && (
                <div className="validation-summary-compact">
                  <span className="validation-icon">🚨</span>
                  <span className="validation-text">
                    Data Loss: {validation.missingRecords.length} records missing
                  </span>
                </div>
              )}

              {/* IRMA File Comparison - 3 columns: DML S3, IES S3, Missing in IES */}
              {pipelineKey === 'irma' && (() => {
                // Extract file lists from each source for comparison
                // API returns: dmlFiles (middle), iesFiles (end), irmaFiles (source - not collected)
                const dmlFiles = uniqByKey(selectedData?.dmlFiles || selectedData?.files || []);
                const iesFiles = uniqByKey(selectedData?.iesFiles || []);
                
                // Get file names for comparison (IRMA source is external / not collected,
                // so the comparison runs DML S3 -> IES S3 only)
                const dmlFileNames = dmlFiles.map(f => f.filename || f.key || f);
                const iesFileNames = iesFiles.map(f => f.filename || f.key || f);
                
                // Use DML as the reference set and compare IES against it.
                const irmaComparison = compareColumnRecords(dmlFileNames, dmlFileNames, iesFileNames);
                // Files present in DML but not (yet) in IES.
                const missingInIes = irmaComparison.missingInDest;
                const dmlByName = {};
                dmlFiles.forEach(f => { dmlByName[f.filename || f.key || f] = f; });
                
                return (
                  <div className="file-list-section">
                    <div className="file-list-header">
                      <h4>📁 File Comparison for {formatDate(selectedDates[pipelineKey])}</h4>
                      {irmaComparison.totalMissing > 0 && (
                        <span className="validation-summary-inline">
                          🚨 {irmaComparison.totalMissing} missing
                        </span>
                      )}
                    </div>
                    <div className="file-comparison-container">
                      {/* Column 1: DML S3 */}
                      <div className="file-column">
                        <div className="file-column-header dml">
                          <span className="column-icon">🪣</span>
                          <span className="column-title">DML S3</span>
                          <span className="column-count">{dmlFileNames.length || '—'}</span>
                          {getColumnStatusBadge(irmaComparison, 'middle')}
                        </div>
                        <div className="file-column-content">
                          {dmlFileNames.length > 0 ? (
                            <ul className="file-name-list">
                              {dmlFiles.slice(0, MAX_RENDER).map((file, index) => (
                                <li key={file.filename || file.key || index} className="file-name-item">
                                  <span className="file-number">{index + 1}.</span>
                                  <span className="file-name" title={file.filename || file.key}>{file.filename || file.key || file}</span>
                                  {file.size && <span className="file-size">{formatSize(file.size)}</span>}
                                </li>
                              ))}
                              {dmlFileNames.length > MAX_RENDER && (
                                <li className="file-name-item more-items">
                                  <span className="file-name">... +{dmlFileNames.length - MAX_RENDER} more</span>
                                </li>
                              )}
                            </ul>
                          ) : (
                            <div className="no-data-placeholder">
                              <span className="placeholder-icon">📭</span>
                              <p>No files</p>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Column 2: IES S3 */}
                      <div className="file-column">
                        <div className="file-column-header ies">
                          <span className="column-icon">🗄️</span>
                          <span className="column-title">IES S3</span>
                          <span className="column-count">{iesFileNames.length || '—'}</span>
                          {getColumnStatusBadge(irmaComparison, 'dest')}
                        </div>
                        <div className="file-column-content">
                          {iesFileNames.length > 0 ? (
                            <ul className="file-name-list">
                              {iesFiles.slice(0, MAX_RENDER).map((file, index) => (
                                <li key={file.filename || file.key || index} className="file-name-item">
                                  <span className="file-number">{index + 1}.</span>
                                  <span className="file-name" title={file.filename || file.key}>{file.filename || file.key || file}</span>
                                  {file.size && <span className="file-size">{formatSize(file.size)}</span>}
                                </li>
                              ))}
                              {iesFileNames.length > MAX_RENDER && (
                                <li className="file-name-item more-items">
                                  <span className="file-name">... +{iesFileNames.length - MAX_RENDER} more</span>
                                </li>
                              )}
                            </ul>
                          ) : (
                            <div className="no-data-placeholder">
                              <span className="placeholder-icon">📭</span>
                              <p>No files</p>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Column 3: Missing in IES */}
                      <div className="file-column">
                        <div className={`file-column-header ${missingInIes.length > 0 ? 'missing-col' : 'no-loss-col'}`}>
                          <span className="column-icon">{missingInIes.length > 0 ? '🚨' : '✅'}</span>
                          <span className="column-title">Missing in IES</span>
                          <span className="column-count">{missingInIes.length}</span>
                        </div>
                        <div className="file-column-content">
                          {missingInIes.length > 0 ? (
                            <ul className="file-name-list">
                              {missingInIes.slice(0, MAX_RENDER).map((name, index) => (
                                <li key={name || index} className="file-name-item missing-item">
                                  <span className="file-number">{index + 1}.</span>
                                  <span className="file-name" title={name}>{name}</span>
                                  {dmlByName[name]?.size && <span className="file-size">{formatSize(dmlByName[name].size)}</span>}
                                </li>
                              ))}
                              {missingInIes.length > MAX_RENDER && (
                                <li className="file-name-item more-items">
                                  <span className="file-name">... +{missingInIes.length - MAX_RENDER} more</span>
                                </li>
                              )}
                            </ul>
                          ) : (
                            <div className="no-data-placeholder all-present">
                              <span className="placeholder-icon">✅</span>
                              <p>All files present in IES</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}

            {/* Workday Employee Comparison Table - 2 data columns + missing: DML S3, IES DynamoDB, Missing in IES */}
            {pipelineKey === 'workday' && (() => {
              // Get employee IDs from DML-MSK collector response (selected day).
              // De-dup on the normalized id so the same id in different formats
              // (zero-padded vs not) is shown only once.
              const dmlEmployeeIds = uniqNormalized(selectedData?.dmlEmployeeIds || selectedData?.dml?.ids || []);
              const iesEmployeeIds = uniqNormalized(selectedData?.iesEmployeeIds || selectedData?.ies?.ids || []);

              // IES DynamoDB is filtered by `modifiedAt` for the selected day, while
              // DML S3 is the firehose objects written that day. An employee whose
              // change crosses midnight (processing lag / timezone) lands in one day
              // on the DML side and the adjacent day on the IES side, so a strict
              // same-day set-diff produces large false "missing" numbers.
              // To avoid that, an employee is only "missing" if its id is absent from
              // IES across ALL loaded days (the visible day-buttons), not just today.
              const allDays = Array.isArray(pipelineData[pipelineKey]) ? pipelineData[pipelineKey] : [];
              const iesIdsAllDays = uniqNormalized(
                allDays.flatMap(d => d?.iesEmployeeIds || d?.ies?.ids || [])
              );
              const iesLookup = iesIdsAllDays.length ? iesIdsAllDays : iesEmployeeIds;

              // Compare the selected day's DML ids against the pooled IES id set.
              const workdayComparison = compareColumnRecords(
                dmlEmployeeIds,
                dmlEmployeeIds,
                iesLookup
              );
              // Prefer the backend's targeted-verification result (checks actual
              // DynamoDB membership per DML id) when available; otherwise fall
              // back to the client-side set-diff. Empty list -> green.
              const backendMissing = selectedData?.missingInIes || selectedData?.ies?.missingIds;
              const usedTargetedVerify = selectedData?.verifyMethod === 'batch_get'
                || selectedData?.verifyMethod === 'query'
                || selectedData?.verifyMethod === 'scan_diff'
                || selectedData?.ies?.method === 'batch_get'
                || selectedData?.ies?.method === 'query'
                || selectedData?.ies?.method === 'scan_diff';
              const missingInIes = uniqNormalized((usedTargetedVerify && Array.isArray(backendMissing))
                ? backendMissing
                : workdayComparison.missingInDest);
              return (
                <div className="file-list-section">
                  <div className="file-list-header">
                    <h4>👥 Employee Comparison for {formatDate(selectedDates[pipelineKey])}</h4>
                    {workdayComparison.totalMissing > 0 && (
                      <span className="validation-summary-inline">
                        🚨 {workdayComparison.totalMissing} missing
                      </span>
                    )}
                  </div>
                  <div className="file-comparison-container">
                    <div className="file-column">
                      <div className="file-column-header dml">
                        <span className="column-icon">🪣</span>
                        <span className="column-title">DML S3</span>
                        <span className="column-count">{dmlEmployeeIds.length || selectedData?.dmlCount || '—'}</span>
                        {getColumnStatusBadge(workdayComparison, 'middle')}
                      </div>
                      <div className="file-column-content">
                        {dmlEmployeeIds.length > 0 ? (
                          <ul className="file-name-list">
                            {dmlEmployeeIds.slice(0, MAX_RENDER).map((empId, index) => (
                              <li key={empId || index} className="file-name-item">
                                <span className="file-number">{index + 1}.</span>
                                <span className="file-name">{empId}</span>
                              </li>
                            ))}
                            {dmlEmployeeIds.length > MAX_RENDER && (
                              <li className="file-name-item more-items">
                                <span className="file-name">... +{dmlEmployeeIds.length - MAX_RENDER} more</span>
                              </li>
                            )}
                          </ul>
                        ) : (
                          <div className="no-data-placeholder">
                            <span className="placeholder-icon">📭</span>
                            <p>{selectedData?.dmlCount > 0 ? `${selectedData.dmlCount} records (IDs not loaded)` : 'No data'}</p>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="file-column">
                      <div className="file-column-header ies">
                        <span className="column-icon">🗄️</span>
                        <span className="column-title">IES DynamoDB</span>
                        <span className="column-count">{iesEmployeeIds.length || selectedData?.iesCount || 0}</span>
                        {getColumnStatusBadge(workdayComparison, 'dest')}
                      </div>
                      <div className="file-column-content">
                        {iesEmployeeIds.length > 0 ? (
                          <ul className="file-name-list">
                            {iesEmployeeIds.slice(0, MAX_RENDER).map((empId, index) => (
                              <li key={empId || index} className="file-name-item">
                                <span className="file-number">{index + 1}.</span>
                                <span className="file-name">{empId}</span>
                              </li>
                            ))}
                            {iesEmployeeIds.length > MAX_RENDER && (
                              <li className="file-name-item more-items">
                                <span className="file-name">... +{iesEmployeeIds.length - MAX_RENDER} more</span>
                              </li>
                            )}
                          </ul>
                        ) : (
                          <div className="no-data-placeholder">
                            <span className="placeholder-icon">📭</span>
                            <p>No employees</p>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="file-column">
                      <div className={`file-column-header ${missingInIes.length > 0 ? 'missing-col' : 'no-loss-col'}`}>
                        <span className="column-icon">{missingInIes.length > 0 ? '🚨' : '✅'}</span>
                        <span className="column-title">Missing in IES</span>
                        <span className="column-count">{missingInIes.length}</span>
                      </div>
                      <div className="file-column-content">
                        {missingInIes.length > 0 ? (
                          <ul className="file-name-list">
                            {missingInIes.slice(0, MAX_RENDER).map((empId, index) => (
                              <li key={empId || index} className="file-name-item missing-item">
                                <span className="file-number">{index + 1}.</span>
                                <span className="file-name">{empId}</span>
                              </li>
                            ))}
                            {missingInIes.length > MAX_RENDER && (
                              <li className="file-name-item more-items">
                                <span className="file-name">... +{missingInIes.length - MAX_RENDER} more</span>
                              </li>
                            )}
                          </ul>
                        ) : (
                          <div className="no-data-placeholder all-present">
                            <span className="placeholder-icon">✅</span>
                            <p>All employees present in IES</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* SAP Integration Data Comparison - 3 columns: DML S3, IES DynamoDB, Missing in IES */}
            {config.category === 'sap' && (() => {
              // DML-MSK collector: dml.ids are the records that reached DML from SAP.
              // De-dup on the normalized id so the same business id in different
              // formats (zero-padded vs not) is shown only once.
              const dmlRecordIds = uniqNormalized(selectedData?.rawRecordIds || selectedData?.dml?.ids || []);
              const iesRecordIds = uniqNormalized(selectedData?.iesRecordIds || selectedData?.ies?.ids || []);

              // Compare DML -> IES directly.
              const sapComparison = compareColumnRecords(dmlRecordIds, dmlRecordIds, iesRecordIds);
              // Prefer the backend's targeted-verification result when available;
              // otherwise fall back to the client-side set-diff. Empty -> green.
              const backendMissing = selectedData?.missingInIes || selectedData?.ies?.missingIds;
              const usedTargetedVerify = selectedData?.verifyMethod === 'batch_get'
                || selectedData?.verifyMethod === 'query'
                || selectedData?.verifyMethod === 'scan_diff'
                || selectedData?.ies?.method === 'batch_get'
                || selectedData?.ies?.method === 'query'
                || selectedData?.ies?.method === 'scan_diff';
              const missingInIes = uniqNormalized((usedTargetedVerify && Array.isArray(backendMissing))
                ? backendMissing
                : sapComparison.missingInDest);
              // Derive displayed counts/badges from the authoritative totals when
              // targeted verification ran (the id arrays above may be capped at
              // MAX_SAMPLE_IDS and are used only to render the visible rows).
              const counts = deriveSapCounts(selectedData, dmlRecordIds.length, iesRecordIds.length);
              // Missing figure shown in the "Missing in IES" column + inline summary:
              // trust the authoritative count for targeted-verify days, else the
              // (deduped) client-side set-diff length.
              const missingDisplayCount = (counts.usedTargetedVerify && counts.missingCount != null)
                ? counts.missingCount
                : missingInIes.length;
              return (
                <div className="file-list-section">
                  <div className="file-list-header">
                    <h4>📊 Data Comparison for {formatDate(selectedDates[pipelineKey])}</h4>
                    {missingDisplayCount > 0 && (
                      <span className="validation-summary-inline">
                        🚨 {missingDisplayCount} missing
                      </span>
                    )}
                  </div>
                  <div className="file-comparison-container">
                    {/* Column 1: DML S3 */}
                    <div className="file-column">
                      <div className="file-column-header dml">
                        <span className="column-icon">🪣</span>
                        <span className="column-title">DML S3</span>
                        <span className="column-count">{counts.dmlColumnCount}</span>
                        {getColumnStatusBadge(sapComparison, 'middle')}
                      </div>
                      <div className="file-column-content">
                        {dmlRecordIds.length > 0 ? (
                          <ul className="file-name-list">
                            {dmlRecordIds.slice(0, MAX_RENDER).map((recordId, index) => (
                              <li key={recordId || index} className="file-name-item">
                                <span className="file-number">{index + 1}.</span>
                                <span className="file-name">{recordId}</span>
                              </li>
                            ))}
                            {dmlRecordIds.length > MAX_RENDER && (
                              <li className="file-name-item more-items">
                                <span className="file-name">... +{dmlRecordIds.length - MAX_RENDER} more</span>
                              </li>
                            )}
                          </ul>
                        ) : (
                          <div className="no-data-placeholder">
                            <span className="placeholder-icon">📭</span>
                            <p>{selectedData?.dmlCount > 0 ? `${selectedData.dmlCount} records (IDs not loaded)` : 'No data'}</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Column 2: IES DynamoDB */}
                    <div className="file-column">
                      <div className="file-column-header ies">
                        <span className="column-icon">🗄️</span>
                        <span className="column-title">IES DynamoDB</span>
                        <span className="column-count">{counts.iesColumnCount}</span>
                        {counts.usedTargetedVerify ? (
                          counts.missingCount > 0 ? (
                            <span className="column-status-badge missing">⚠ {counts.missingCount} missing</span>
                          ) : (counts.iesColumnCount > 0 ? (
                            <span className="column-status-badge match">✓ OK</span>
                          ) : null)
                        ) : getColumnStatusBadge(sapComparison, 'dest')}
                      </div>
                      {selectedData?.iesIdField && (
                        <div className="column-field-name">
                          <span className="field-label">ID Field:</span>
                          <span className="field-value">{selectedData.iesIdField}</span>
                        </div>
                      )}
                      <div className="file-column-content">
                        {iesRecordIds.length > 0 ? (
                          <ul className="file-name-list">
                            {iesRecordIds.slice(0, MAX_RENDER).map((recordId, index) => (
                              <li key={recordId || index} className="file-name-item">
                                <span className="file-number">{index + 1}.</span>
                                <span className="file-name">{recordId}</span>
                              </li>
                            ))}
                            {iesRecordIds.length > MAX_RENDER && (
                              <li className="file-name-item more-items">
                                <span className="file-name">... +{iesRecordIds.length - MAX_RENDER} more</span>
                              </li>
                            )}
                          </ul>
                        ) : (
                          <div className="no-data-placeholder">
                            <span className="placeholder-icon">📭</span>
                            <p>No records</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Column 3: Missing in IES */}
                    <div className="file-column">
                      <div className={`file-column-header ${missingDisplayCount > 0 ? 'missing-col' : 'no-loss-col'}`}>
                        <span className="column-icon">{missingDisplayCount > 0 ? '🚨' : '✅'}</span>
                        <span className="column-title">Missing in IES</span>
                        <span className="column-count">{missingDisplayCount}</span>
                      </div>
                      <div className="file-column-content">
                        {missingInIes.length > 0 ? (
                          <ul className="file-name-list">
                            {missingInIes.slice(0, MAX_RENDER).map((recordId, index) => (
                              <li key={recordId || index} className="file-name-item missing-item">
                                <span className="file-number">{index + 1}.</span>
                                <span className="file-name">{recordId}</span>
                              </li>
                            ))}
                            {missingInIes.length > MAX_RENDER && (
                              <li className="file-name-item more-items">
                                <span className="file-name">... +{missingInIes.length - MAX_RENDER} more</span>
                              </li>
                            )}
                          </ul>
                        ) : (
                          <div className="no-data-placeholder all-present">
                            <span className="placeholder-icon">✅</span>
                            <p>All records present in IES</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}
            </div> {/* End of unified-pipeline-widget */}

            {/* Quick Date Selection */}
            {dataArray.length > 0 && (
              <div className="date-quick-select-compact">
                <div className="date-buttons-compact">
                  {dataArray.slice(0, 7).map(item => {
                    // Same authoritative count as the header (handles the SAP
                    // 1000-sample cap); falls back to distinct sample / raw count.
                    const count = getDayDisplayCount(pipelineKey, config, item);
                    return (
                      <button
                        key={item.date}
                        className={`date-btn-compact ${item.date === selectedDates[pipelineKey] ? 'active' : ''} ${count > 0 ? 'has-data' : 'no-data'}`}
                        onClick={() => setSelectedDates(prev => ({ ...prev, [pipelineKey]: item.date }))}
                      >
                        <span className="date-btn-date-compact">{formatDate(item.date)}</span>
                        <span className="date-btn-count-compact">{getStatusIcon(item.status, count)} {count}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  // Group pipelines by category
  const pipelinesByCategory = {
    irma: ['irma'],
    workday: ['workday'],
    sap: ['networks', 'activityMilestones', 'characteristics', 'projects', 'salesorder']
  };

  return (
    <div className="ies-dashboard">
      {/* Header */}
      <div className="dashboard-header">
        <div className="header-left">
          <h2>🔗 IES Integrations</h2>
          <p className="header-subtitle">End-to-End Data Pipeline Monitoring with Data Validation</p>
        </div>
      </div>

      {/* Integration Overview Summary */}
      <div className="top-summary-card">
        <div className="top-summary-header">
          <h3>📊 Integration Overview</h3>
          <span className="overview-date">{Object.keys(pipelineData).filter(k => pipelineData[k]?.length > 0).length} / {Object.keys(PIPELINE_CONFIG).length} pipelines active</span>
        </div>
        <div className="category-summary">
          <div className="category-item">
            <span className="category-icon">📁</span>
            <span className="category-name">IRMA</span>
            <span className="category-count">1 pipeline</span>
          </div>
          <div className="category-item">
            <span className="category-icon">👥</span>
            <span className="category-name">Workday</span>
            <span className="category-count">1 pipeline</span>
          </div>
          <div className="category-item">
            <span className="category-icon">🏭</span>
            <span className="category-name">SAP</span>
            <span className="category-count">5 pipelines</span>
          </div>
        </div>
      </div>

      {/* IRMA Integration */}
      <div className="pipeline-category-section">
        <h3 className="category-title">📁 IRMA Integration</h3>
        {pipelinesByCategory.irma.map(key => renderPipelineOverview(key, PIPELINE_CONFIG[key]))}
      </div>

      {/* Workday Integration */}
      <div className="pipeline-category-section">
        <h3 className="category-title">👥 Workday Integration</h3>
        {pipelinesByCategory.workday.map(key => renderPipelineOverview(key, PIPELINE_CONFIG[key]))}
      </div>

      {/* SAP Integrations */}
      <div className="pipeline-category-section">
        <h3 className="category-title">🏭 SAP Integrations (5 Pipelines)</h3>
        <p className="category-description">DML Raw S3 → DML Processed S3 → IES data flow monitoring</p>
        {pipelinesByCategory.sap.map(key => renderPipelineOverview(key, PIPELINE_CONFIG[key]))}
      </div>

      {lastRefresh && <div className="last-refresh">Last refreshed: {lastRefresh.toLocaleString()}</div>}
    </div>
  );
};

export default IESIntegrationDashboard;
